import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const wave = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const ripple = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
`;

const LoadingContainer = styled.div<{ $isExiting: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a0f1f 0%, #1d388d 50%, #2d4da3 100%);
  background-size: 200% 200%;
  animation: ${props => props.$isExiting ? fadeOut : fadeIn} 0.5s ease-in-out,
             ${shimmer} 8s ease-in-out infinite;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
`;

const BackgroundParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  opacity: 0.3;
`;

const Particle = styled.div<{ $delay: number; $duration: number; $left: number }>`
  position: absolute;
  width: 4px;
  height: 4px;
  background: white;
  border-radius: 50%;
  top: 100%;
  left: ${props => props.$left}%;
  animation: ${float} ${props => props.$duration}s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  opacity: 0.6;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
`;

const LogoContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 300px;
  height: 300px;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const LogoBackground = styled.div`
  position: absolute;
  width: 220px;
  height: 220px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 249, 255, 0.98) 100%);
  border-radius: 50%;
  box-shadow: 0 8px 32px rgba(255, 255, 255, 0.3),
              0 0 60px rgba(29, 56, 141, 0.4),
              inset 0 2px 0 rgba(255, 255, 255, 0.8);
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, #00d4ff, #0099cc);
    border-radius: 50%;
    z-index: -1;
    animation: ${rotate} 3s linear infinite;
  }
`;

const RippleEffect = styled.div<{ $delay: number }>`
  position: absolute;
  width: 220px;
  height: 220px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  animation: ${ripple} 2s ease-out infinite;
  animation-delay: ${props => props.$delay}s;
`;

const Logo = styled.img`
  width: 180px;
  height: auto;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
  position: relative;
  z-index: 2;
`;

const SpinnerRing = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  width: 250px;
  height: 250px;
  border: 3px solid transparent;
  border-top-color: rgba(255, 255, 255, 0.8);
  border-bottom-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: ${rotate} 2s linear infinite;
  opacity: 0.7;
  z-index: 0;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
`;

const SpinnerRingOuter = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  width: 280px;
  height: 280px;
  border: 3px solid transparent;
  border-right-color: rgba(0, 212, 255, 0.6);
  border-left-color: rgba(0, 212, 255, 0.6);
  border-radius: 50%;
  animation: ${rotate} 3s linear infinite reverse;
  opacity: 0.6;
  z-index: 0;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.4);
`;

const LoadingText = styled.div`
  margin-top: 40px;
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 3px;
  text-transform: uppercase;
  text-align: center;
  width: 100%;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5),
               0 0 20px rgba(255, 255, 255, 0.3);
`;

const DotsContainer = styled.span`
  display: inline-block;
  width: 30px;
  text-align: left;
`;

const Dot = styled.span<{ $delay: number }>`
  animation: ${wave} 1.4s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  display: inline-block;
  color: #00d4ff;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.8);
`;

const SubText = styled.div`
  margin-top: 12px;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 2px;
  text-align: center;
  width: 100%;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

interface LoadingScreenProps {
  isLoading: boolean;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ isLoading }) => {
  const [isExiting, setIsExiting] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(true);

  React.useEffect(() => {
    if (!isLoading && shouldRender) {
      setIsExiting(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 500); // Match fadeOut animation duration
      return () => clearTimeout(timer);
    }
  }, [isLoading, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  // Generate random particles
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    delay: Math.random() * 3,
    duration: 3 + Math.random() * 4,
    left: Math.random() * 100
  }));

  return (
    <LoadingContainer $isExiting={isExiting}>
      <BackgroundParticles>
        {particles.map(p => (
          <Particle key={p.id} $delay={p.delay} $duration={p.duration} $left={p.left} />
        ))}
      </BackgroundParticles>

      <LogoContainer>
        <RippleEffect $delay={0} />
        <RippleEffect $delay={0.5} />
        <RippleEffect $delay={1} />
        <LogoBackground />
        <Logo src="/logo.png" alt="Waldevar Floating Logo" />
        <SpinnerRingOuter />
        <SpinnerRing />
      </LogoContainer>

      <LoadingText>
        Loading
        <DotsContainer>
          <Dot $delay={0}>.</Dot>
          <Dot $delay={0.2}>.</Dot>
          <Dot $delay={0.4}>.</Dot>
        </DotsContainer>
      </LoadingText>
      <SubText>Metrics & Stats</SubText>
    </LoadingContainer>
  );
};

export default LoadingScreen;
